import React, { useState, useEffect, useCallback } from "react";
import "./GeoLocations.css";
import { exportToCSV } from "../../utils/exportCSV";
import privateApi from "../../apis/privateApi";
import Spinner from "../../components/Spinner/Spinner";
import { FiPlus, FiMinus } from "react-icons/fi";

/* ─── Level metadata ─────────────────────────────────────────── */
const LEVEL_LABELS = ["Senatorial District", "LGA", "Ward"];

const capitalize = str => str.replace(/\b\w/g, c => c.toUpperCase());

/* ─── GeoNode ────────────────────────────────────────────────── */
const GeoNode = ({ item, level, collectData, fetchChildren }) => {
    const [expanded, setExpanded] = useState(false);
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        collectData({ level, name: item.name, code: item.code });
    }, [item, level, collectData]);

    const handleExpand = async () => {
        if (!item.hasChildren || loading) return;
        if (expanded) { setExpanded(false); return; }
        if (children.length === 0) {
            try {
                setLoading(true);
                const res = await fetchChildren(item._id, level);
                setChildren(res?.data?.data || []);
            } catch (err) {
                console.error("Failed to fetch children:", err);
            } finally { setLoading(false); }
        }
        setExpanded(true);
    };

    const indent = (level - 1) * 28 + 18;
    const levelClass = `geo-level-${Math.min(level, 3)}`;

    return (
        <div className="geo-node">
            <div className="geo-node-header" style={{ paddingLeft: indent }} onClick={handleExpand}>
                {item.hasChildren && (
                    loading
                        ? <span className="geo-mini-spinner" />
                        : <button className="geo-toggle-btn" onClick={e => { e.stopPropagation(); handleExpand(); }}>
                            {expanded ? <FiMinus size={11} /> : <FiPlus size={11} />}
                        </button>
                )}

                <span className={`geo-level-pill ${levelClass}`}>{LEVEL_LABELS[level - 1] || `L${level}`}</span>
                <span className="geo-node-label">{capitalize(item.name)}</span>
                <span className="geo-code">{item.code}</span>
            </div>

            {expanded && !loading && (
                <div className="geo-children">
                    {children.map(child => (
                        <GeoNode key={child._id} item={child} level={level + 1}
                            collectData={collectData} fetchChildren={fetchChildren} />
                    ))}
                </div>
            )}
        </div>
    );
};

/* ─── MasterData page ────────────────────────────────────────── */
const MasterData = () => {
    const [flatData, setFlatData] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [loadingDistricts, setLoadingDistricts] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                setLoadingDistricts(true);
                const res = await privateApi.get("/api/senatorial-districts");
                setDistricts(res?.data?.data || []);
            } catch (err) {
                console.error("Failed to load senatorial districts", err);
            } finally { setLoadingDistricts(false); }
        })();
    }, []);

    const collectData = useCallback(item => {
        setFlatData(prev => {
            const exists = prev.some(i => i.code === item.code && i.level === item.level);
            if (exists) return prev;
            return [...prev, item];
        });
    }, []);

    const fetchChildren = async (id, level) => {
        switch (level) {
            case 1: return await privateApi.get(`/api/lgas?senatorialDistrictId=${id}`);
            case 2: return await privateApi.get(`/api/wards?lgaId=${id}`);
            default: return { data: { data: [] } };
        }
    };

    return (
        <div className="geo-page">
            {/* Header */}
            <div className="geo-header">
                <div className="geo-header-left">
                    <h1>Geographic Master Data</h1>
                    <p>Senatorial Districts · LGAs · Wards — expand to explore hierarchy</p>
                </div>
                <button className="geo-export-btn" onClick={() => exportToCSV(flatData)} disabled={flatData.length === 0}>
                    ⬇ Export CSV
                </button>
            </div>

            {/* Tree card */}
            <div className="geo-tree-card">
                {loadingDistricts ? (
                    <div style={{ padding: "60px 20px", display: "flex", justifyContent: "center" }}>
                        <Spinner loadingInfo="Loading Senatorial Districts…" height={40} size={40} color="#006B3C" />
                    </div>
                ) : districts.length === 0 ? (
                    <div className="geo-empty">No senatorial districts available.</div>
                ) : (
                    <div className="geo-tree-inner">
                        {districts.map(district => (
                            <GeoNode key={district._id} item={district} level={1}
                                collectData={collectData} fetchChildren={fetchChildren} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MasterData;